import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AutocompletePropsType, ItemForAutocompleteType } from 'components/CommonAutoComplete/types'
import { useAppDispatch, useAppSelector } from 'hooks'
import { currentUserSelect } from 'store/selectors'
import useDebounce from 'hooks/useDebounce'
import { BUILDS_PER_PAGE } from 'pages/BuildingsPage/helpers'
import { CommonResponseType } from 'types'
import { Paragraph } from 'ui-kit/components/Typography/Typography'
import { Autocomplete } from '@mui/material'
import { getOptions } from 'lib/common'
import { CustomTextField } from 'ui-kit/components/CustomTextField'
import { CustomShowMore } from 'ui-kit/components/CustomShowMore'
import { CustomRemovedBox } from 'ui-kit/components/CustomRemovedBox'
import { getAllRoomsForAutocomplete } from 'api/rooms'
import Icons from '../../assets/icons'
import { Room } from '../../slices/floorPlanViever'
import { LeaseStatus } from '../../slices/users'

export const RoomAutoComplete = ({
    label,
    handleBlur,
    errorText,
    currentValue,
    readOnly = false,
    disabled = false,
    textFieldClassName,
    isSearch = false,
    disableClearable = false,
    textFieldPlaceholder,
    width = '100%',
    onChangeCallback,
    onDeleteItemClick,
    multiple,
    idDependence,
    isBuildingLabel = false,
    filteredValues,
    disableActiveAndAmenity,
}: AutocompletePropsType & {
    isBuildingLabel?: boolean
    filteredValues?: Array<string>
    disableActiveAndAmenity?: boolean
}) => {
    const dispatch = useAppDispatch()
    const user = useAppSelector(currentUserSelect)
    const [values, setValues] = useState<Room[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [count, setCount] = useState(0)
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounce(search, 500)

    const onInputChange = (event: any, newInputValue: string) => {
        setSearch(newInputValue)
        setPage(1)
    }

    const disabledCondition = count <= page * BUILDS_PER_PAGE || disableClearable

    const isOptionEqualToValue = (option: ItemForAutocompleteType, value: ItemForAutocompleteType) =>
        option?.value === value?.value

    const onShowMoreClick = (e: React.MouseEvent<HTMLParagraphElement>) => {
        e.preventDefault()
        setPage((prev) => prev + 1)
        getAllValues(page + 1)
    }

    const getAllValues = async (currentPage: number) => {
        setIsLoading(true)
        try {
            const res: CommonResponseType<Room> = await dispatch(
                getAllRoomsForAutocomplete({
                    page: currentPage,
                    search,
                    idDependence,
                }),
            ).unwrap()

            setCount(res?.count)
            setValues((prev) => (currentPage === 1 ? res.rows : [...res.rows, ...prev]))
        } catch (error: any) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    const renderOption = useCallback(
        (props: React.HTMLAttributes<HTMLLIElement>, option: ItemForAutocompleteType) => {
            const currentRoom = values.find((value) => value.id === option?.value)

            if (option.value === 'Not found') {
                return (
                    <li style={{ pointerEvents: 'none' }} {...props}>
                        <Icons.NotFoundSVG color="#848a9b" style={{ marginRight: '.4rem' }} />
                        <Paragraph size={'s'} color="grey">
                            Not found
                        </Paragraph>
                    </li>
                )
            }

            return (
                <li
                    {...props}
                    aria-disabled={
                        disableActiveAndAmenity &&
                        (currentRoom?.leases.some((item) => item.leaseStatus === LeaseStatus.Active) ||
                            currentRoom?.isAmenity)
                    }
                >
                    <div>
                        <Paragraph size={'s'} style={{ marginRight: '.4rem' }}>
                            {option?.label}
                        </Paragraph>
                        {!!currentRoom?.floor.building.name && isBuildingLabel && (
                            <Paragraph size={'xxs'} color="grey">
                                {currentRoom.floor.building.name}
                            </Paragraph>
                        )}
                    </div>
                </li>
            )
        },
        [values, currentValue],
    )

    useEffect(() => {
        if (!user || typeof idDependence === 'undefined' || search) {
            return
        }

        getAllValues(page)
    }, [user, search, JSON.stringify(idDependence)])

    useEffect(() => {
        if (typeof idDependence === 'undefined') {
            return
        }
        if (debouncedSearch) {
            getAllValues(page)
        }
    }, [debouncedSearch, JSON.stringify(idDependence)])

    const getOptionLabel = useCallback((option: ItemForAutocompleteType) => {
        return option?.label ?? ''
    }, [])

    const renderTags = useCallback(
        (value: ItemForAutocompleteType[]) => {
            if (onDeleteItemClick) {
                return ''
            }

            if (value?.length === 1) {
                return (
                    <Paragraph
                        size={'s'}
                        style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {value[0]?.label}
                    </Paragraph>
                )
            } else {
                return (
                    <Paragraph size={'s'} style={{ display: 'flex', gap: '.4rem' }}>
                        {value?.length}
                        <Paragraph size={'s'} color={'grey'}>
                            (rooms)
                        </Paragraph>
                    </Paragraph>
                )
            }
        },
        [onDeleteItemClick],
    )

    const getPlaceholder = useMemo(() => {
        setSearch('')
        if (multiple) {
            return currentValue?.length ? '' : textFieldPlaceholder
        }
        return !Array.isArray(currentValue) && currentValue?.value ? '' : textFieldPlaceholder
    }, [multiple, currentValue])

    const optionsRender = useMemo(() => {
        const filteredOptions = filteredValues
            ? values.filter((itemValue) => !filteredValues.some((item) => item === itemValue.id))
            : values
        return getOptions(filteredOptions, 'id', 'name')
    }, [values])

    return (
        <>
            <Autocomplete
                value={currentValue}
                readOnly={readOnly}
                disabled={disabled}
                onChange={(event, newValue) => {
                    onChangeCallback(newValue as ItemForAutocompleteType[] & ItemForAutocompleteType, values)
                    setSearch('')
                }}
                openOnFocus={true}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        flexWrap: 'nowrap',
                        padding: 0,
                    },
                }}
                multiple={multiple}
                inputValue={search}
                onInputChange={onInputChange}
                options={optionsRender?.length > 0 ? optionsRender : [{ value: 'Not found', label: 'Not found' }]}
                freeSolo
                renderTags={renderTags}
                getOptionLabel={(option) => getOptionLabel(option as ItemForAutocompleteType)}
                renderInput={(params) => (
                    <CustomTextField
                        label={label}
                        onBlur={handleBlur}
                        params={params}
                        errorText={errorText}
                        loading={isLoading}
                        className={textFieldClassName}
                        placeholder={getPlaceholder}
                        isSearch={isSearch}
                    />
                )}
                renderOption={renderOption}
                disableCloseOnSelect={multiple}
                filterOptions={(x) => x}
                limitTags={1}
                style={{ width }}
                loading={isLoading}
                isOptionEqualToValue={isOptionEqualToValue}
                disableClearable={disableClearable}
                PaperComponent={({ children }) => (
                    <CustomShowMore
                        onShowMoreClick={onShowMoreClick}
                        children={children}
                        text={'Show more'}
                        disabled={disabledCondition}
                    />
                )}
            />
            {onDeleteItemClick &&
                multiple &&
                currentValue?.map((item) => {
                    return (
                        <CustomRemovedBox
                            id={item?.value ?? ''}
                            title={item?.label ?? ''}
                            onDeleteClick={onDeleteItemClick}
                        />
                    )
                })}
        </>
    )
}
